import React, { useEffect, useRef } from "react";

// =============================================================================
// StickyBottomBar — viewport-anchored bottom action / summary bar
// =============================================================================
// Replaces the duplicated `fixed bottom-0 inset-x-0 bg-ink-raised border-t
// border-line z-20` className that was scattered across 17 screens (Quote
// Builder totals, AP/permit/HOP/JMP action footers, form runners, etc).
//
// Why a component, not just a className: the bar overlays scrollable content,
// so the scroll container needs `padding-bottom` equal to the bar's height
// or the last row of content gets hidden behind the bar at end-of-scroll.
// Inlining the className meant nobody owned that side-effect, and James found
// the bug on the Quote Builder totals row (HAR-26-1065 DEEPWATER screenshot).
//
// Mechanism: on mount, observe own offsetHeight via ResizeObserver and publish
// it to `--sticky-bar-h` on `document.documentElement`. AppShell's <main>
// reads that variable into its padding-bottom. On mobile the padding composes
// additively with the existing 5rem reserved for MobileBottomNav.
// On unmount, the variable is removed so screens without a sticky bar revert
// to zero clearance.
// =============================================================================

interface Props {
  /** Optional extra utility classes (padding, flex layout, gap, etc). */
  className?: string;
  children: React.ReactNode;
}

export function StickyBottomBar({ className, children }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () => {
      root.style.setProperty("--sticky-bar-h", `${el.offsetHeight}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--sticky-bar-h");
    };
  }, []);

  return (
    <div
      ref={ref}
      // 22 Oct 2026 mobile audit: on a phone the bar used to sit on top of the
      // bottom nav (z-20 over z-9) with its buttons in the home-indicator strip
      // — "bottom buttons are cut off". It now stacks above the nav
      // (--nav-clear, set in index.css); <main> already reserves nav + bar.
      className={`fixed bottom-0 max-md:bottom-[var(--nav-clear)] inset-x-0 bg-ink-raised border-t border-line z-20${className ? ` ${className}` : ""}`}
    >
      {children}
    </div>
  );
}
